import React, { useMemo, useState, useEffect, useRef } from "react";
import { Select, SIZE as SSIZE } from "baseui/select";
import { Button, SHAPE, SIZE as BSIZE } from "baseui/button";
import { Block } from "baseui/block";
import { FlexGrid, FlexGridItem } from "baseui/flex-grid";
import { styled } from "baseui";
import { connect } from "react-redux";
import { executePostProcessing, fetchPostProcessOperationOverview, fetchModelRunAssets } from "../../redux/model-run/ModelRunThunk";
import { updateselectedModelRun } from "../../redux/model-run/ModelRunActions";
import PipelineProgressStepCompnent from "../../components/pipeline-progress-step/PipelineProgressStepCompnent";
import LogViewerComponent from "../../components/log-viewer/LogViewerComponent";
import { useSmartPolling } from "../../hooks/useSmartPolling";
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton } from "baseui/modal";
import { FormControl } from "baseui/form-control";
import { FaEdit, FaSave, FaDownload, FaEye, FaCode, FaUndo } from "react-icons/fa";
import Editor from "@monaco-editor/react";
import { Tabs, Tab } from "baseui/tabs-motion";
import PipelineFlowWithProvider, { PARSE_STRATEGIES } from "../../components/code-view-editor/PipelineFlowComponent";
import ModelRunSelectorComponent from "../../components/model-run-selector/ModelRunSelectorComponent";
import { useModelRunSelection } from "../../hooks/useModelRunSelection";

const RUN_EXTS = ["zip", "vtk"];
const POLL_INTERVAL = 5000; // 5 seconds

const Wrapper = styled(Block, {
  border: "1px dashed #AAA",
  borderRadius: "8px",
});

const Divider = styled("div", {
  width: "4px",
  backgroundColor: "#AAA",
  margin: "0 16px",
});

// Custom wrapper for the pipeline preview to fit in modal
const PipelinePreviewWrapper = styled("div", {
  height: "100%",
  width: "100%",
  "& > div > div": {
    height: "100% !important",
    border: "none !important",
    minHeight: "400px !important",
  }
});

const selectOverrides = { ControlContainer: { style: { width: "100%" } } };

const dialogOverrides = {
  Dialog: {
    style: {
      width: "700px",
      maxWidth: "70vw",
      height: "auto",
    },
  },
};

const tabContentStyle = {
  padding: "0px",
  height: "500px", // Reduced height for smaller modal
  overflow: "auto",
};

const ICON_STYLE = { marginRight: "8px" };

const EditorContainer = styled(Block, {
  minHeight: "500px",
  height: "100%",
  border: "1px solid #CCC",
  borderRadius: "4px",
});

const PreviewContainer = styled(Block, {
  minHeight: "500px",
  height: "100%",
  border: "1px solid #CCC",
  borderRadius: "4px",
  overflow: "hidden",
});

function ModelerPostProcessingRunPipeline({
  activeModeler,
  selectedModelRun,
  postProcessOverview,
  assets = [],
  onExecutePostProcessing,
  onFetchPipelineStatus,
  onFetchModelRunAssets,
  onUpdateSelectedModelRun,
}) {
  const editorRef = useRef(null);
  const runAssets = useMemo(
    () =>
      assets.filter((a) =>
        RUN_EXTS.includes(a.filename.split(".").pop().toLowerCase())
      ),
    [assets]
  );

  const runOptions = runAssets.map((a) => ({
    id: a.filename,
    label: a.filename,
    asset: a,
  }));

  const [runSel, setRunSel] = useState([]);
  const [runErr, setRunErr] = useState("");
  const [operationId, setOperationId] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelineResult, setPipelineResult] = useState(null);
  const [polling, setPolling] = useState(false);
  
  // Pipeline editor state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [pipelineCode, setPipelineCode] = useState("");
  const [originalPipelineCode, setOriginalPipelineCode] = useState("");
  const [activeTab, setActiveTab] = useState("0");
  const [lastFinishedState, setLastFinishedState] = useState(false);
  const [childKey, setChildKey] = useState(Date.now());

  // Force update function similar to template editor
  const forceUpdate = () => {
    setChildKey(Date.now());
  };

  // Use the new ModelRun selection hook for asset fetching
  const {
    selectedModelRunId: hookSelectedModelRunId,
    selectedModelRunData: hookSelectedModelRunData,
    modelRunAssets: hookModelRunAssets,
    loading: hookLoading,
    error: hookError,
    handleModelRunSelect: hookHandleModelRunSelect,
    modelRunOptions: hookModelRunOptions
  } = useModelRunSelection({
    modelerId: activeModeler?.id,
    fetchAssets: true,
    fetchDetails: false,
    assetPageSize: 50,
    onModelRunSelect: (runData, runId) => {
      console.log(`ModelRun selected: ${runData?.runName} (ID: ${runId})`);
      console.log(`Assets found: ${hookModelRunAssets?.length || 0}`);
    },
    onError: (error) => {
      console.error('ModelRun selection error:', error);
    }
  });

  useSmartPolling({
       enabled: Boolean(operationId) || postProcessOverview?.finished,
        interval: POLL_INTERVAL,
        fetcher: () => onFetchPipelineStatus(activeModeler.id, operationId),
        done: () => postProcessOverview?.finished,
      });

  // Check for operationId in selectedModelRun updates
  useEffect(() => {
    if (postProcessOverview?.operationId) {
      setOperationId(postProcessOverview.operationId);
      setPolling(true);
    }
    if (postProcessOverview?.result && Object.keys(postProcessOverview.result).length > 0){
        setPipelineResult(postProcessOverview.result);
        setPolling(false);        
    }
  }, [postProcessOverview]);

  // Refresh assets when pipeline finishes successfully
  useEffect(() => {
    const isFinished = postProcessOverview?.finished || false;
    
    // Check if pipeline just finished (transition from not finished to finished)
    if (isFinished && !lastFinishedState && activeModeler?.id && selectedModelRun?.id) {
      console.log("Pipeline finished successfully, refreshing assets...");
      // Refresh the model run assets to show newly generated files
      onFetchModelRunAssets(activeModeler.id, selectedModelRun.id, 50, null);
    }
    
    // Update the last finished state
    setLastFinishedState(isFinished);
  }, [postProcessOverview?.finished, lastFinishedState, activeModeler?.id, selectedModelRun?.id, onFetchModelRunAssets]);

  // Initialize pipeline code when selectedModelRun changes
  useEffect(() => {
    const newPipelineCode = selectedModelRun?.postProcess || "";
    setPipelineCode(newPipelineCode);
    setOriginalPipelineCode(newPipelineCode);
    // Force update editor to clear any cached content
    forceUpdate();
  }, [selectedModelRun]);

  // Force update when content changes significantly
  useEffect(() => {
    forceUpdate();
  }, [pipelineCode, isEditModalOpen]);

  const handleRunPipeline = async () => {
    setRunErr("");
    const asset = runSel[0]?.asset;
    if (!asset) return setRunErr("Choose a .zip or .vtk file first");
    if (!activeModeler?.id) return setRunErr("No active modeler selected");
    if (!selectedModelRun?.id) return setRunErr("No model run in context");

    try {
      setPolling(false);
      setOperationId(null);
      await onExecutePostProcessing(
        activeModeler.id,
        selectedModelRun.id,
        asset.id
      );
      // The operationId will be set in the useEffect when it becomes available
    } catch (error) {
      setRunErr("Failed to start pipeline");
      console.error("Pipeline execution error:", error);
    }
  };

  const handleEditPipeline = () => {
    // Force update to ensure clean editor state
    forceUpdate();
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    // Reset to original code if not saved
    setPipelineCode(originalPipelineCode);
  };

  const handleSavePipeline = () => {
    // Update the model run locally with the new pipeline code
    const updatedModelRun = {
      ...selectedModelRun,
      postProcess: pipelineCode,
    };
    
    onUpdateSelectedModelRun(updatedModelRun);
    
    // Update local state and close modal
    setOriginalPipelineCode(pipelineCode);
    setIsEditModalOpen(false);
    console.log("Pipeline code updated locally");
  };

  const handleDownloadPipeline = () => {
    const blob = new Blob([pipelineCode], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'postprocess.yaml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleResetPipeline = () => {
    setPipelineCode(originalPipelineCode);
  };

  const handleEditorChange = (value) => {
    setPipelineCode(value || "");
  };

  // Handle editor mount to ensure proper content initialization
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    // Explicitly set the content to ensure clean state
    editor.setValue(pipelineCode || "");
    
    // Configure editor options for consistency
    editor.updateOptions({
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      fontSize: 14,
      lineNumbers: 'on',
      wordWrap: 'on',
      automaticLayout: true,
      readOnly: false,
      folding: true,
      lineDecorationsWidth: 10,
      lineNumbersMinChars: 3,
      glyphMargin: false,
      useTabStops: false,
      tabSize: 2,
      insertSpaces: true,
      detectIndentation: true,
      trimAutoWhitespace: true,
      largeFileOptimizations: true,
    });
  };

  const isPipelineModified = pipelineCode !== originalPipelineCode;

  return (
    <Wrapper width="100%" padding="scale600">
      {/* Enhanced ModelRun Selection with Asset Fetching */}
      <Block marginBottom="scale600">
        <ModelRunSelectorComponent
          modelerId={activeModeler?.id}
          fetchAssets={true}
          fetchDetails={false}
          assetPageSize={50}
          label="Select ModelRun for Pipeline Processing"
          placeholder="Choose a model run to process..."
          size={SSIZE.compact}
          showAssetCount={true}
          showLoadingState={true}
          showError={true}
          onModelRunSelect={(runData, runId) => {
            console.log(`Pipeline: ModelRun selected - ${runData?.runName} (${runId})`);
            // You can add additional logic here when a ModelRun is selected
          }}
          onError={(error) => {
            console.error('Pipeline ModelRun selection error:', error);
          }}
        />
      </Block>

      <FlexGrid
        flexGridColumnCount={3}
        flexGridColumnGap="scale600"
        alignItems="center"
      >
        <FlexGridItem>
          <Select
            size={SSIZE.compact}
            options={runOptions}
            value={runSel}
            placeholder="Choose a .zip or .vtk file"
            onChange={({ value }) => setRunSel(value)}
            clearable={false}
            overrides={selectOverrides}
            error={!!runErr}
          />
        </FlexGridItem>
        <FlexGridItem display="flex" justifyContent="center">
          <Button
            onClick={handleEditPipeline}
            shape={SHAPE.pill}
            size={BSIZE.compact}
            startEnhancer={<FaEdit />}
            disabled={!selectedModelRun?.postProcess}
          >
            Edit Pipeline
          </Button>
        </FlexGridItem>
        <FlexGridItem display="flex" justifyContent="flex-end">
          <Button
            onClick={handleRunPipeline}
            shape={SHAPE.pill}
            size={BSIZE.compact}
            disabled={runOptions.length === 0}
            isLoading={polling}
          >
            {polling ? "Pipeline Running..." : "Run Pipeline"}
          </Button>
        </FlexGridItem>
      </FlexGrid>
      {runErr && (
        <Block marginTop="scale400" color="negative500">
          {runErr}
        </Block>
      )}

      <Block marginTop="scale800" display="flex" alignItems="stretch">
        <Block flex="1">
          <PipelineProgressStepCompnent 
            yamlString={selectedModelRun?.postProcess || ""} 
            finished = {postProcessOverview?.finished || false}
            completedSteps={postProcessOverview?.completedSteps || []}
          />
        </Block>
        <Divider />
        <Block flex="2">
          <LogViewerComponent 
            logs={postProcessOverview?.logs || []}
          />
        </Block>
      </Block>

      {/* Pipeline Editor Modal */}
      <Modal 
        onClose={handleCloseEditModal} 
        isOpen={isEditModalOpen} 
        size="large"
        overrides={dialogOverrides}
      >
        <ModalHeader>Edit Post-Processing Pipeline</ModalHeader>
        <ModalBody>
          <Tabs
            activeKey={activeTab}
            onChange={({ activeKey }) => setActiveTab(activeKey)}
            overrides={{
              TabContent: {
                style: tabContentStyle,
              },
            }}
          >
            <Tab
              title={
                <Block display="flex" alignItems="center">
                  <FaCode style={ICON_STYLE} />
                  Edit YAML
                </Block>
              }
              key="0"
            >
              <Block height="500px" display="flex" flexDirection="column">
                <FormControl>
                  <EditorContainer key={childKey}>
                    <Editor
                      key={`editor-${childKey}`}
                      height="100%"
                      width="100%"
                      language="yaml"
                      theme="vs-light"
                      value={pipelineCode}
                      onChange={handleEditorChange}
                      onMount={handleEditorDidMount}
                    />
                  </EditorContainer>
                </FormControl>
                

              </Block>
            </Tab>
            
            <Tab
              title={
                <Block display="flex" alignItems="center">
                  <FaEye style={ICON_STYLE} />
                  Preview
                </Block>
              }
              key="1"
            >
              <Block height="500px" display="flex" flexDirection="column">
                <FormControl>
                  <PreviewContainer key={`preview-${childKey}`}>
                    <PipelinePreviewWrapper>
                      <PipelineFlowWithProvider 
                        key={`pipeline-preview-${childKey}`}
                        yamlText={pipelineCode}
                        parseStrategy={PARSE_STRATEGIES.PIPELINE_STEPS}
                        containerStyle={{ height: '100%' }}
                        enableAutoFit={true}
                        showNodeAccordion={true}
                      />
                    </PipelinePreviewWrapper>
                  </PreviewContainer>
                </FormControl>
              </Block>
            </Tab>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="tertiary" onClick={handleCloseEditModal} size={BSIZE.mini}>
            Cancel
          </ModalButton>
          <ModalButton
            onClick={handleResetPipeline}
            startEnhancer={<FaUndo />}
            kind="secondary"
            disabled={!isPipelineModified}
            size={BSIZE.mini}
          >
            Reset
          </ModalButton>
          <ModalButton
            onClick={handleDownloadPipeline}
            startEnhancer={<FaDownload />}
            kind="secondary"
            size={BSIZE.mini}
          >
            Download
          </ModalButton>
          <ModalButton
            onClick={handleSavePipeline}
            startEnhancer={<FaSave />}
            disabled={!isPipelineModified}
            size={BSIZE.mini}
          >
            Save Changes
          </ModalButton>
        </ModalFooter>
      </Modal>
    </Wrapper>
  );
}

const mapStateToProps = (state) => ({
  activeModeler: state.modeler.activeModeler,
  selectedModelRun: state.modelRun?.selectedModelRun,
  assets: state.modelRun?.selectedModelRun?.assets || [],
  postProcessOverview: state.modelRun?.executePostProcessOverview || {},
});

const mapDispatchToProps = (dispatch) => ({
  onExecutePostProcessing: (modelerId, modelRunId, assetItemId) =>
    dispatch(executePostProcessing(modelerId, modelRunId, assetItemId)),
  onFetchPipelineStatus: (modelerId, operationId) =>
    dispatch(fetchPostProcessOperationOverview(modelerId, operationId)),
  onFetchModelRunAssets: (modelerId, modelRunId, limit, cursor) =>
    dispatch(fetchModelRunAssets(modelerId, modelRunId, limit, cursor)),
  onUpdateSelectedModelRun: (modelRun) =>
    dispatch(updateselectedModelRun({ modelrun: modelRun })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ModelerPostProcessingRunPipeline);